import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLeads } from '../../../context/LeadContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../../api/config';
import { get, post, del, put } from '../../../api/apiClient';
import {
  ArrowLeft,
  Save,
  X,
  User,
  Building,
  MapPin,
  Tag,
  FileText,
  AlertCircle,
  ChevronDown,
  Search,
  Upload,
  Trash2,
  Loader2,
  Video,
  Link as LinkIcon
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { toast } from 'react-hot-toast';
import PermissionGate from '../../../components/common/PermissionGate';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../../components/common/LocationSelect/LocationSelect';
import { fetchPincodeDetails } from '../../../api/pincodeService';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  sanitizeEmailInput,
  sanitizeAddressInput,
  sanitizeDescriptionInput,
  validateNumberInput,
  validatePhoneInput,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';

const ManualLeadEntry = ({ onBack, leadId: propLeadId, onSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editLeadId = propLeadId || searchParams.get('edit');
  const isEditMode = !!editLeadId;

  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSalesExecutive =
    user?.role === USER_ROLES.SALES_EXECUTIVE ||
    user?.role === USER_ROLES.EXPORT_EXECUTIVE ||
    user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER ||
    user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const { getLeadById, loading } = useLeads();
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    alternatePhone: '',

    // Company Information
    company: '',
    website: '',
    industry: '',
    companySize: '',
    annualRevenue: '',
    companyDocuments: [],

    // Address Information
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: '',

    // Lead Information
    source: 'manual_entry',
    companySource: 'agrosaf',
    status: 'new',
    region: '',
    leadValue: '',
    budget: '',
    timeline: '',
    productRequirement: '',
    decisionMaker: '',
    category: '',
    owner: '',
    assignedTo: '', // Store user ID for backend
    leadVideoStatus: '',
    leadOrderStatus: '',
    leadCallStatus: '',
    leadInterest: '',

    // Additional Information
    description: '',
    notes: '',
    tags: [],
    statusChangeReason: '',
    scoreLabel: 'cold'
  });

  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('personal');
  const [originalStatus, setOriginalStatus] = useState(null); // Track original status to prevent changing converted leads
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [verifierSearchTerms, setVerifierSearchTerms] = useState({});
  const [openVerifierDropdownIndex, setOpenVerifierDropdownIndex] = useState(null);
  const userDropdownRef = useRef(null);
  const hasLoadedRef = useRef(false); // Track if lead data has been loaded

  const [isDragging, setIsDragging] = useState(null);
  const [documentUploadState, setDocumentUploadState] = useState({});
  const [documentDeleteState, setDocumentDeleteState] = useState({});
  const prevEditIdRef = useRef(null);

  // Reset hasLoaded when edit ID changes
  useEffect(() => {
    if (editLeadId !== prevEditIdRef.current) {
      hasLoadedRef.current = false;
      prevEditIdRef.current = editLeadId;
    }
  }, [editLeadId]);

  // Pre-fill phone number from search parameter if not in edit mode
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    if (!isEditMode && phoneParam && !hasLoadedRef.current) {
      setFormData(prev => ({ ...prev, phone: phoneParam }));
      hasLoadedRef.current = true; // Mark as loaded so we don't overwrite user changes
    }
  }, [searchParams, isEditMode]);

  // Auto-assign lead to logged-in sales executive on create
  useEffect(() => {
    if (!isEditMode && isSalesExecutive && user) {
      const currentUserId = user.id || user.user || user._id;
      if (currentUserId && !formData.assignedTo) {
        const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
        setFormData(prev => ({
          ...prev,
          assignedTo: currentUserId,
          owner: userName || prev.owner
        }));
      }
    }
  }, [isEditMode, isSalesExecutive, user, formData.assignedTo]);

  // Geographic state for LocationSelect
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isPincodeLoading, setIsPincodeLoading] = useState(false);
  const lastSearchedKeyRef = useRef('');
  const [selectedLocation, setSelectedLocation] = useState({
    country: null,
    state: null,
    city: null
  });

  // React Query for Master Data
  const { data: masterData, isLoading: isLoadingMasters } = useQuery({
    queryKey: ['leadMasterData'],
    queryFn: async () => {
      const [indResult, dmResult, srcResult, docTypeResult, categoryResult, statusResult, videoResult, orderResult, callResult, interestResult, sizeResult] = await Promise.all([
        get(`${API_ENDPOINTS.CRM.CONFIG.INDUSTRIES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.DECISION_MAKERS}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_SOURCES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_DOCUMENT_TYPES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_CATEGORIES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_STATUSES_DROPDOWN}`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_VIDEO_STATUSES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_ORDER_STATUSES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_CALL_STATUSES}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_INTERESTS}?active=true`),
        get(`${API_ENDPOINTS.CRM.CONFIG.COMPANY_SIZES}?active=true`)
      ]);
      return {
        industries: indResult.data || [],
        decisionMakers: dmResult.data || [],
        sources: srcResult.data || [],
        documentTypes: docTypeResult.data || [],
        leadCategories: categoryResult.data || [],
        leadStatuses: statusResult.data || [],
        leadVideoStatuses: videoResult.data || [],
        leadOrderStatuses: orderResult.data || [],
        leadCallStatuses: callResult.data || [],
        leadInterests: interestResult.data || [],
        companySizes: sizeResult.data || []
      };
    }
  });

  const masterIndustries = masterData?.industries || [];
  const masterDecisionMakers = masterData?.decisionMakers || [];
  const masterSources = masterData?.sources || [];
  const masterLeadDocumentTypes = masterData?.documentTypes || [];
  const masterLeadCategories = masterData?.leadCategories || [];
  const masterLeadStatuses = masterData?.leadStatuses || [];
  const masterLeadVideoStatuses = masterData?.leadVideoStatuses || [];
  const masterLeadOrderStatuses = masterData?.leadOrderStatuses || [];
  const masterLeadCallStatuses = masterData?.leadCallStatuses || [];
  const masterLeadInterests = masterData?.leadInterests || [];
  const masterCompanySizes = masterData?.companySizes || [];

  // Map lead source from backend response (name, code, or object) to masterSource _id for select dropdown
  const mapLeadSourceToMasterId = (sourceVal, sources = masterSources) => {
    if (!sourceVal) return '';
    if (typeof sourceVal === 'object' && sourceVal !== null) {
      return sourceVal._id || sourceVal.id || '';
    }
    const str = String(sourceVal).trim();
    if (!str || !sources || sources.length === 0) return str;

    // Match by _id
    const byId = sources.find(s => String(s._id) === str || String(s.id) === str);
    if (byId) return byId._id || byId.id;

    // Match by name (backend getLeadById returns source name e.g. "Manual Entry")
    const byName = sources.find(s => s.name && s.name.toLowerCase() === str.toLowerCase());
    if (byName) return byName._id || byName.id;

    // Match by code (e.g. "LSRC-0001")
    const byCode = sources.find(s => s.leadSourceCode && s.leadSourceCode.toUpperCase() === str.toUpperCase());
    if (byCode) return byCode._id || byCode.id;

    return str;
  };

  // Sync formData.source to matching masterSource _id once masterSources loads
  useEffect(() => {
    if (masterSources.length > 0 && formData.source) {
      const mappedId = mapLeadSourceToMasterId(formData.source, masterSources);
      if (mappedId && mappedId !== formData.source) {
        setFormData(prev => ({ ...prev, source: mappedId }));
      }
    }
  }, [masterSources]);

  const closedWonStatusName = useMemo(() => {
    return masterLeadStatuses.find((status) => status.value === 'closed_won')?.name || 'Won';
  }, [masterLeadStatuses]);
  const isStatusChangedInEdit = useMemo(() => {
    return Boolean(editLeadId && originalStatus && formData.status && formData.status !== originalStatus);
  }, [editLeadId, originalStatus, formData.status]);

  // React Query for Users
  const { data: userData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['crmUsers', userSearchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (userSearchTerm) params.append('search', userSearchTerm.trim());
      params.append('limit', '50');
      params.append('roleFilter', 'sales');

      if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
        params.append('managerId', user._id || user.id);
      }

      const response = await get(`${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`);
      if (response.responseCode === 200) {
        return (response.data?.users || []).map(user => ({
          id: user.user || user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          role: user.role || '',
          department: user.department || '',
          designation: user.designation || ''
        })).sort((a, b) => a.name.localeCompare(b.name));
      }
      return [];
    },
    enabled: isUserDropdownOpen || hasLoadedRef.current
  });

  const filteredUsers = useMemo(() => (userData || []).slice(0, 10), [userData]);

  const activeVerifierSearchTerm = openVerifierDropdownIndex !== null
    ? (verifierSearchTerms[openVerifierDropdownIndex] || '')
    : '';

  const { data: superAdminUserData, isLoading: isLoadingSuperAdmins } = useQuery({
    queryKey: ['crmSuperAdminUsers', activeVerifierSearchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (activeVerifierSearchTerm) params.append('search', activeVerifierSearchTerm.trim());
      params.append('limit', '50');
      params.append('roleFilter', 'super_admin');

      const response = await get(`${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`);
      if (response.responseCode === 200) {
        return (response.data?.users || []).map(user => ({
          id: user.user || user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          role: user.role || '',
          designation: user.designation || 'Super Admin'
        })).sort((a, b) => a.name.localeCompare(b.name));
      }
      return [];
    },
    enabled: openVerifierDropdownIndex !== null || hasLoadedRef.current
  });

  const filteredSuperAdmins = useMemo(() => (superAdminUserData || []).slice(0, 10), [superAdminUserData]);

  // React Query for Lead Data (Edit Mode)
  const { data: fetchedLead, isLoading: isLoadingLead } = useQuery({
    queryKey: ['lead', editLeadId],
    queryFn: async () => {
      const result = await getLeadById(editLeadId);
      return result;
    },
    enabled: isEditMode && !!editLeadId,
    staleTime: 0, // Always fetch fresh data when entering edit mode
    cacheTime: 0  // Don't cache lead details to avoid stale hydration
  });

  // Sync fetched lead data to formData state
  useEffect(() => {
    if (fetchedLead && !hasLoadedRef.current) {
      const result = fetchedLead;
      setFormData({
        firstName: result.firstName || '',
        lastName: result.lastName || '',
        email: result.email || '',
        phone: result.phone || '',
        alternatePhone: result.alternatePhone || '',
        company: result.company || '',
        website: result.website || '',
        industry: result.industryId || result.industry?._id || result.industry || '',
        companySize: result.companySize?.companySizeCode || result.companySize?._id || result.companySize || (result.employeeCount ? mapEmployeeCountToCompanySize(result.employeeCount) : ''),
        annualRevenue: result.annualRevenue ? String(result.annualRevenue) : '',
        companyDocuments: Array.isArray(result.companyDocuments) && result.companyDocuments.length > 0
          ? result.companyDocuments.map(doc => {
            const pdfData = doc.companyPdf;
            let normalizedPdf = pdfData;

            // Normalize string URLs to object format for consistent handling
            if (typeof pdfData === 'string' && pdfData && !pdfData.startsWith('{')) {
              const parts = pdfData.split('/');
              const filename = parts[parts.length - 1] || 'Document.pdf';
              normalizedPdf = {
                url: pdfData,
                name: filename,
                originalName: filename,
                public_id: doc.companyPdfPublicId || ''
              };
            }

            return {
              fileType: doc.fileType || (doc.videoUrl ? 'video' : 'document'),
              documentTypeId: doc.documentTypeId || (typeof doc.documentType === 'object' ? doc.documentType?._id : (typeof doc.documentType === 'string' ? doc.documentType : '')),
              documentTypeName: doc.documentTypeName || (typeof doc.documentType === 'object' ? doc.documentType?.name : (doc.documentType || '')),
              documentMethod: doc.documentMethod || (doc.documentLink ? 'link' : 'upload'),
              documentLink: doc.documentLink || '',
              companyPdf: normalizedPdf || '',
              companyPdfPublicId: doc.companyPdfPublicId || (normalizedPdf?.public_id || ''),
              videoUrl: doc.videoUrl || '',
              videoPublicId: doc.videoPublicId || '',
              videoMetadata: doc.videoMetadata || '',
              verificationStatus: doc.verificationStatus || 'pending',
              verificationAssignedTo: doc.verificationAssignedTo?._id || doc.verificationAssignedTo || '',
              verificationAssignedToName: doc.verificationAssignedTo
                ? `${doc.verificationAssignedTo.firstName || ''} ${doc.verificationAssignedTo.lastName || ''}`.trim() || doc.verificationAssignedTo.email || ''
                : '',
              verificationAssignedToEmail: doc.verificationAssignedTo?.email || '',
              approvedBy: doc.approvedBy || null,
              approvedAt: doc.approvedAt || '',
              certificateNumber: doc.certificateNumber || '',
              expiryDate: doc.expiryDate || ''
            };
          })
          : (result.companyPdf ? (() => {
            const pdfData = result.companyPdf;
            let normalizedPdf = pdfData;

            if (typeof pdfData === 'string' && pdfData && !pdfData.startsWith('{')) {
              const parts = pdfData.split('/');
              const filename = parts[parts.length - 1] || 'Legacy Attachment.pdf';
              normalizedPdf = {
                url: pdfData,
                name: filename,
                originalName: filename,
                public_id: result.companyPdfPublicId || ''
              };
            }

            return [{
              fileType: 'document',
              documentTypeId: '',
              documentTypeName: 'Legacy Attachment',
              documentMethod: 'upload',
              documentLink: '',
              companyPdf: normalizedPdf,
              companyPdfPublicId: result.companyPdfPublicId || (normalizedPdf?.public_id || ''),
              certificateNumber: '',
              expiryDate: ''
            }];
          })() : []),
        address: (typeof result.address === 'string' ? result.address : result.address?.street) || '',
        city: result.city || (result.address?.city) || '',
        state: result.state || (result.address?.state) || '',
        pincode: result.pincode || (result.address?.pincode) || '',
        country: result.country || (result.address?.country) || '',
        source: mapLeadSourceToMasterId(result.sourceId || result.source, masterSources),
        companySource: result.companySource || 'agrosaf',
        status: result.stage || 'new',
        region: mapRegionToDropdown(result.region || result.address?.state || ''),
        leadValue: result.leadValue ? String(result.leadValue) : '',
        budget: result.budget ? String(result.budget) : '',
        timeline: mapTimelineToDropdown(result.timeline || ''),
        productRequirement: result.productRequirement || '',
        decisionMaker: result.decisionMakerId || result.decisionMaker?._id || result.decisionMaker || '',
        category: result.categoryId || result.category?._id || result.category || '',
        owner: result.owner || '',
        assignedTo: result.assignedToId || result.assignedTo?._id || (typeof result.assignedTo === 'string' ? result.assignedTo : '') || '',
        leadVideoStatus: result.leadVideoStatus?._id || result.leadVideoStatus || '',
        leadOrderStatus: result.leadOrderStatus?._id || result.leadOrderStatus || '',
        leadCallStatus: result.leadCallStatus?._id || result.leadCallStatus || '',
        leadInterest: result.leadInterest?._id || result.leadInterest || '',
        description: result.notes || result.description || '',
        notes: result.internalNotes || result.notes || '',
        tags: Array.isArray(result.tags) ? result.tags : (result.tags ? [result.tags] : []),
        scoreLabel: result.scoreLabel || 'cold'
      });

      if (result.industry && typeof result.industry === 'object') setLeadIndustryInfo(result.industry);
      if (result.decisionMaker && typeof result.decisionMaker === 'object') setLeadDecisionMakerInfo(result.decisionMaker);
      if (result.source && typeof result.source === 'object') setLeadSourceInfo(result.source);
      if (result.leadVideoStatus && typeof result.leadVideoStatus === 'object') setLeadVideoStatusInfo(result.leadVideoStatus);
      if (result.leadOrderStatus && typeof result.leadOrderStatus === 'object') setLeadOrderStatusInfo(result.leadOrderStatus);
      if (result.leadCallStatus && typeof result.leadCallStatus === 'object') setLeadCallStatusInfo(result.leadCallStatus);
      if (result.leadInterest && typeof result.leadInterest === 'object') setLeadInterestInfo(result.leadInterest);
      setOriginalStatus(result.stage);

      let locationToHydrate = (result.locations && result.locations.length > 0) ? result.locations[0] : null;
      if (locationToHydrate) {
        setSelectedLocation({
          country: locationToHydrate.country || null,
          state: locationToHydrate.address?.state || null,
          city: locationToHydrate.address?.city || null
        });
      } else if (result.country || result.address?.country) {
        const countryVal = result.country || result.address?.country;
        const foundCountry = Country.getAllCountries().find(c => c.name === countryVal || c.isoCode === countryVal);
        if (foundCountry) {
          const countryObj = { id: foundCountry.isoCode, name: foundCountry.name, isoCode: foundCountry.isoCode };
          setSelectedLocation(prev => ({ ...prev, country: countryObj }));
          const stateVal = result.state || result.address?.state;
          const cityVal = result.city || result.address?.city;

          if (stateVal) {
            const foundState = State.getStatesOfCountry(foundCountry.isoCode).find(s => s.name === stateVal || s.isoCode === stateVal);
            if (foundState) {
              const stateObj = { id: foundState.isoCode, name: foundState.name, isoCode: foundState.isoCode, countryCode: foundState.countryCode };
              setSelectedLocation(prev => ({ ...prev, state: stateObj }));

              if (cityVal) {
                const foundCity = City.getCitiesOfState(foundCountry.isoCode, foundState.isoCode).find(c => c.name === cityVal);
                setSelectedLocation(prev => ({
                  ...prev,
                  city: foundCity ? { ...foundCity, id: `${foundCity.name}-${foundCity.stateCode}`, name: foundCity.name } : { id: cityVal, name: cityVal }
                }));
              }
            } else if (cityVal) {
              // State not found but city exists
              setSelectedLocation(prev => ({ ...prev, city: { id: cityVal, name: cityVal } }));
            }
          } else if (cityVal) {
            // No state, but has city
            setSelectedLocation(prev => ({ ...prev, city: { id: cityVal, name: cityVal } }));
          }
        }
      }
      hasLoadedRef.current = true;
    }
  }, [fetchedLead, editLeadId]);

  const [leadIndustryInfo, setLeadIndustryInfo] = useState(null);
  const [leadDecisionMakerInfo, setLeadDecisionMakerInfo] = useState(null);
  const [leadSourceInfo, setLeadSourceInfo] = useState(null);
  const [leadVideoStatusInfo, setLeadVideoStatusInfo] = useState(null);
  const [leadOrderStatusInfo, setLeadOrderStatusInfo] = useState(null);
  const [leadCallStatusInfo, setLeadCallStatusInfo] = useState(null);
  const [leadInterestInfo, setLeadInterestInfo] = useState(null);

  const buildCloudinaryDeleteUrl = (publicId, resourceType = 'raw') =>
    `${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${encodeURIComponent(publicId)}&resource_type=${encodeURIComponent(resourceType)}`;

  const deleteCloudinaryFile = async (publicId, resourceType = 'raw') => {
    if (!publicId) return true;
    try {
      const result = await del(buildCloudinaryDeleteUrl(publicId, resourceType));
      return result.responseCode === 200;
    } catch (error) {
      return false;
    }
  };

  const cleanupDocumentUpload = async (doc) => {
    if (!doc) return;

    const deletions = [];
    if (doc.companyPdfPublicId) {
      deletions.push(deleteCloudinaryFile(doc.companyPdfPublicId, 'raw'));
    }
    if (doc.videoPublicId) {
      deletions.push(deleteCloudinaryFile(doc.videoPublicId, 'video'));
    }

    if (deletions.length > 0) {
      const results = await Promise.allSettled(deletions);
      const failed = results.some(result => result.status === 'rejected' || result.value === false);
      if (failed) {
        toast.error('File removed from form, but cloud cleanup failed for one upload.');
      }
    }
  };

  const getDocumentStateKey = (docIndex) => docIndex === null || docIndex === undefined ? 'legacy' : String(docIndex);

  const setDocumentUploadInProgress = (docIndex, isUploading) => {
    const key = getDocumentStateKey(docIndex);
    setDocumentUploadState(prev => {
      const next = { ...prev };
      if (isUploading) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const setDocumentDeleteInProgress = (docIndex, isDeleting) => {
    const key = getDocumentStateKey(docIndex);
    setDocumentDeleteState(prev => {
      const next = { ...prev };
      if (isDeleting) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const isDocumentUploading = (docIndex) => Boolean(documentUploadState[getDocumentStateKey(docIndex)]);
  const isDocumentDeleting = (docIndex) => Boolean(documentDeleteState[getDocumentStateKey(docIndex)]);

  const getDocumentVerifierLabel = (doc) => {
    const assignedTo = doc?.verificationAssignedTo?._id || doc?.verificationAssignedTo || '';
    if (!assignedTo) return 'Select decision maker';

    const selectedUser = (superAdminUserData || []).find(admin => String(admin.id) === String(assignedTo));
    if (selectedUser) {
      return `${selectedUser.name}${selectedUser.email ? ` (${selectedUser.email})` : ''}`;
    }

    if (doc.verificationAssignedToName || doc.verificationAssignedToEmail) {
      return `${doc.verificationAssignedToName || doc.verificationAssignedToEmail}${doc.verificationAssignedToEmail && doc.verificationAssignedToName ? ` (${doc.verificationAssignedToEmail})` : ''}`;
    }

    if (doc.verificationAssignedTo && typeof doc.verificationAssignedTo === 'object') {
      const name = `${doc.verificationAssignedTo.firstName || ''} ${doc.verificationAssignedTo.lastName || ''}`.trim();
      return `${name || doc.verificationAssignedTo.email || 'Selected decision maker'}${doc.verificationAssignedTo.email && name ? ` (${doc.verificationAssignedTo.email})` : ''}`;
    }

    return 'Selected decision maker';
  };

  const handleVerifierSelect = (index, admin) => {
    setFormData(prev => {
      const newDocs = [...(prev.companyDocuments || [])];
      if (newDocs[index]) {
        newDocs[index] = {
          ...newDocs[index],
          verificationAssignedTo: admin.id,
          verificationAssignedToName: admin.name || '',
          verificationAssignedToEmail: admin.email || ''
        };
      }
      return { ...prev, companyDocuments: newDocs };
    });

    setErrors(prev => {
      const next = { ...prev };
      delete next[`companyDocuments_${index}_verifier`];
      return next;
    });
    setOpenVerifierDropdownIndex(null);
    setVerifierSearchTerms(prev => ({ ...prev, [index]: '' }));
  };

  // Mutations
  const uploadPdfMutation = useMutation({
    mutationFn: async ({ file, docIndex, previousPublicId, previousResourceType }) => {
      const pdfFormData = new FormData();
      pdfFormData.append('file', file);
      pdfFormData.append('folder', file.type?.startsWith('video/') ? 'customer-verification' : 'company-documents');
      const result = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, pdfFormData);
      if (result.responseCode !== 200) throw new Error(result.responseMessage || 'Upload failed');
      return { result, docIndex, file, previousPublicId, previousResourceType };
    },
    onMutate: ({ docIndex }) => {
      setDocumentUploadInProgress(docIndex, true);
    },
    onSuccess: ({ result, docIndex, file, previousPublicId, previousResourceType }) => {
      const pdfMetadata = {
        name: file.name,
        originalName: file.name,
        size: file.size,
        mimetype: file.type,
        url: result.data.url,
        public_id: result.data.public_id
      };
      const isVideo = file.type?.startsWith('video/');

      if (docIndex !== null) {
        setFormData(prev => {
          const newDocs = [...(prev.companyDocuments || [])];
          if (newDocs[docIndex]) {
            newDocs[docIndex] = isVideo
              ? {
                ...newDocs[docIndex],
                fileType: 'video',
                videoUrl: result.data.url,
                videoPublicId: result.data.public_id,
                videoMetadata: pdfMetadata,
                companyPdf: '',
                companyPdfPublicId: '',
                verificationStatus: 'pending',
                approvedBy: null,
                approvedAt: '',
                rejectedBy: null,
                rejectedAt: '',
                rejectionReason: ''
              }
              : {
                ...newDocs[docIndex],
                fileType: 'document',
                companyPdf: pdfMetadata,
                companyPdfPublicId: result.data.public_id,
                videoUrl: '',
                videoPublicId: '',
                videoMetadata: ''
              };
          }
          return { ...prev, companyDocuments: newDocs };
        });
      } else {
        setFormData(prev => ({
          ...prev,
          companyPdf: pdfMetadata,
          companyPdfPublicId: result.data.public_id
        }));
      }
      if (previousPublicId && previousPublicId !== result.data.public_id) {
        deleteCloudinaryFile(previousPublicId, previousResourceType).then((deleted) => {
          if (!deleted) {
            toast.error('New file uploaded, but old cloud file cleanup failed.');
          }
        });
      }
      toast.success(`${isVideo ? 'Video' : 'Document'} uploaded successfully`);
    },
    onError: (error) => {
      toast.error(error.message || 'An error occurred during file upload.');
    },
    onSettled: (_data, _error, variables) => {
      setDocumentUploadInProgress(variables?.docIndex, false);
    }
  });

  const deletePdfMutation = useMutation({
    mutationFn: async ({ publicId, docIndex, resourceType = 'raw' }) => {
      if (!publicId) return { docIndex };
      const result = await del(buildCloudinaryDeleteUrl(publicId, resourceType));
      if (result.responseCode !== 200) throw new Error(result.responseMessage || 'Delete failed');
      return { docIndex, resourceType };
    },
    onMutate: ({ docIndex }) => {
      setDocumentDeleteInProgress(docIndex, true);
    },
    onSuccess: ({ docIndex, resourceType }) => {
      setFormData(prev => {
        const newDocs = [...(prev.companyDocuments || [])];
        if (newDocs[docIndex]) {
          newDocs[docIndex] = resourceType === 'video'
            ? { ...newDocs[docIndex], videoUrl: '', videoPublicId: '', videoMetadata: '', verificationStatus: 'pending', approvedBy: null, approvedAt: '', rejectedBy: null, rejectedAt: '', rejectionReason: '' }
            : { ...newDocs[docIndex], companyPdf: '', companyPdfPublicId: '' };
        }
        return { ...prev, companyDocuments: newDocs };
      });
      toast.success('File removed successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'An error occurred while removing the document.');
    },
    onSettled: (_data, _error, variables) => {
      setDocumentDeleteInProgress(variables?.docIndex, false);
    }
  });

  const leadMutation = useMutation({
    mutationFn: async (payload) => {
      if (isEditMode && editLeadId) {
        return await put(`${API_ENDPOINTS.CRM.LEADS}/${editLeadId}`, payload);
      } else {
        return await post(API_ENDPOINTS.CRM.LEADS, payload);
      }
    },
    onSuccess: (result) => {
      if (result.responseCode === 200 || result.responseCode === 201) {
        toast.success(`Lead ${isEditMode ? 'updated' : 'created'} successfully!`);
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        if (isEditMode && editLeadId) {
          queryClient.invalidateQueries({ queryKey: ['lead', editLeadId] });
        }
        navigate(-1);
      } else {
        throw new Error(result.responseMessage || `Unable to ${isEditMode ? 'update' : 'create'} lead.`);
      }
    },
    onError: (error) => {
      toast.error(error.message || 'An error occurred.');
    }
  });

  const handlePdfUpload = (e, docIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;
    const doc = formData.companyDocuments?.[docIndex];
    const isVideo = doc?.fileType === 'video';
    if (isVideo && !file.type?.startsWith('video/')) return toast.error('Only video files are allowed.');

    const allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const isAllowedDoc =
      allowedMimeTypes.includes(file.type?.toLowerCase()) ||
      /\.(pdf|jpg|jpeg|png)$/i.test(file.name);

    if (!isVideo && !isAllowedDoc) {
      return toast.error('Only PDF and image files (JPG, JPEG, PNG) are allowed.');
    }

    if (file.size > 100 * 1024 * 1024) return toast.error('File size must be less than 100MB.');
    uploadPdfMutation.mutate({
      file,
      docIndex,
      previousPublicId: isVideo ? doc?.videoPublicId : doc?.companyPdfPublicId,
      previousResourceType: isVideo ? 'video' : 'auto'
    });
    e.target.value = '';
  };

  const getDocumentDownloadFilename = (doc) => {
    if (!doc || !doc.companyPdf) return "document.pdf";

    // If companyPdf is the metadata object
    if (typeof doc.companyPdf === 'object' && doc.companyPdf !== null) {
      const fallbackName = doc.companyPdf.originalName || doc.companyPdf.name || "document";
      if (/\.[a-zA-Z0-9]+$/.test(fallbackName)) return fallbackName;

      const mimeType = (doc.companyPdf.mimetype || doc.companyPdf.mimeType || "").toLowerCase();
      if (mimeType === "application/pdf") return `${fallbackName}.pdf`;
      return `${fallbackName}.pdf`;
    }

    // Fallback for string URLs
    const pdfUrl = String(doc.companyPdf || '');
    const parts = pdfUrl.split('/');
    const filename = parts[parts.length - 1] || "document.pdf";
    if (/\.[a-zA-Z0-9]+$/.test(filename)) return filename;
    return `${filename.pdf ? filename : filename + '.pdf'}`;
  };

  const handleDocumentDownload = async (doc) => {
    if (!doc || !doc.companyPdf) return;

    const pdfData = doc.companyPdf;
    const url = typeof pdfData === 'object' && pdfData !== null ? pdfData.url : pdfData;
    const filename = getDocumentDownloadFilename(doc);

    try {
      // Use toast to show processing
      const downloadToast = toast.loading('Preparing download...');

      const response = await authFetch(url);
      if (!response.ok) throw new Error("Failed to fetch document");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(downloadToast);
    } catch (error) {
      // Fallback: Just open in new tab if blob download fails
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(null);

    if (isDocumentUploading(index)) {
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const mockEvent = { target: { files: e.dataTransfer.files } };
      handlePdfUpload(mockEvent, index);
    }
  };

  const handlePdfDelete = (docIndex) => {
    const doc = formData.companyDocuments[docIndex];
    deletePdfMutation.mutate({
      publicId: doc?.fileType === 'video' ? doc?.videoPublicId : doc?.companyPdfPublicId,
      docIndex,
      resourceType: doc?.fileType === 'video' ? 'video' : 'raw'
    });
  };

  const handleDocumentChange = (index, field, value) => {
    const existingDoc = formData.companyDocuments?.[index];

    const shouldCleanupUpload =
      existingDoc &&
      ((field === 'fileType' && value !== existingDoc.fileType) ||
        (field === 'documentMethod' && value === 'link' && existingDoc.documentMethod === 'upload'));

    if (shouldCleanupUpload) {
      cleanupDocumentUpload(existingDoc);
    }

    setFormData(prev => {
      const newDocs = [...(prev.companyDocuments || [])];
      if (newDocs[index]) {
        newDocs[index] = { ...newDocs[index], [field]: value };

        // Clear the other method's data to prevent confusion
        if (field === 'documentMethod') {
          if (value === 'upload') {
            newDocs[index].documentLink = '';
          } else {
            newDocs[index].companyPdf = '';
            newDocs[index].companyPdfPublicId = '';
            newDocs[index].videoUrl = '';
            newDocs[index].videoPublicId = '';
            newDocs[index].videoMetadata = '';
            newDocs[index].verificationStatus = 'pending';
            newDocs[index].approvedBy = null;
            newDocs[index].approvedAt = '';
            newDocs[index].rejectedBy = null;
            newDocs[index].rejectedAt = '';
            newDocs[index].rejectionReason = '';
          }
        }
        if (field === 'documentLink' && (newDocs[index].fileType || 'document') === 'video') {
          newDocs[index].verificationStatus = 'pending';
          newDocs[index].approvedBy = null;
          newDocs[index].approvedAt = '';
          newDocs[index].rejectedBy = null;
          newDocs[index].rejectedAt = '';
          newDocs[index].rejectionReason = '';
        }
        if (field === 'fileType') {
          newDocs[index].documentMethod = 'upload';
          newDocs[index].documentLink = '';
          newDocs[index].companyPdf = '';
          newDocs[index].companyPdfPublicId = '';
          newDocs[index].videoUrl = '';
          newDocs[index].videoPublicId = '';
          newDocs[index].videoMetadata = '';
          newDocs[index].verificationStatus = 'pending';
          if (value === 'document') {
            newDocs[index].verificationAssignedTo = '';
            newDocs[index].verificationAssignedToName = '';
            newDocs[index].verificationAssignedToEmail = '';
          }
          if (value === 'video') {
            newDocs[index].certificateNumber = '';
            newDocs[index].expiryDate = '';
          }
        }
      }
      return { ...prev, companyDocuments: newDocs };
    });
  };

  const addDocument = () => {
    setFormData(prev => ({
      ...prev,
      companyDocuments: [
        { fileType: 'document', documentTypeId: '', documentTypeName: '', documentType: '', documentMethod: 'upload', documentLink: '', companyPdf: '', companyPdfPublicId: '', videoUrl: '', videoPublicId: '', videoMetadata: '', verificationStatus: 'pending', verificationAssignedTo: '', verificationAssignedToName: '', verificationAssignedToEmail: '', certificateNumber: '', expiryDate: '' },
        ...(prev.companyDocuments || [])
      ]
    }));
  };

  const removeDocument = (index) => {
    cleanupDocumentUpload(formData.companyDocuments?.[index]);
    const newDocs = [...formData.companyDocuments];
    newDocs.splice(index, 1);
    setFormData(prev => ({ ...prev, companyDocuments: newDocs }));
  };

  const regions = ['North', 'South', 'East', 'West', 'Central', 'North-East', 'North-West', 'South-East', 'South-West'];
  const timelines = ['Immediate (0-30 days)', 'Short-term (1-3 months)', 'Medium-term (3-6 months)', 'Long-term (6+ months)'];

  const sourceOptions = [
    { value: '', label: 'Select Source' }, // Default option
    ...masterSources.map(s => ({ value: s.leadSourceCode || s._id || s.id, label: s.name })),
  ];

  // Helper function to format number with Indian currency format (lakhs/crores)
  const formatNumberWithCommas = (value) => {
    if (!value || value === '') return '';
    // Remove all non-digit characters
    const numericValue = String(value).replace(/\D/g, '');
    if (!numericValue) return '';

    // Indian numbering system: last 3 digits, then groups of 2
    // Example: 1234567 → 12,34,567 | 100000 → 1,00,000 | 1000 → 1,000
    const numStr = numericValue;
    const lastThree = numStr.slice(-3);
    const rest = numStr.slice(0, -3);

    if (rest === '') {
      return lastThree;
    }

    // Group remaining digits in pairs of 2 from right to left
    const restReversed = rest.split('').reverse().join('');
    const restGroups = restReversed.match(/.{1,2}/g) || [];
    const restFormatted = restGroups
      .map(chunk => chunk.split('').reverse().join(''))
      .reverse()
      .join(',');

    return restFormatted + ',' + lastThree;
  };

  // Helper function to parse formatted number (remove commas)
  const parseFormattedNumber = (value) => {
    if (!value || value === '') return '';
    // Remove all non-digit characters
    return String(value).replace(/\D/g, '');
  };

  // Removed manual fetch functions as they are replaced by useQuery
  // Removed manual loading states as they are replaced by React Query states

  // Load all countries on mount
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map(c => ({
      ...c,
      id: c.isoCode,
      name: c.name
    }));
    setCountryOptions(formattedCountries);
  }, []);

  useEffect(() => {
    if (selectedLocation.country) {
      const states = State.getStatesOfCountry(selectedLocation.country.isoCode);
      const formattedStates = states.map(s => ({
        ...s,
        id: s.isoCode,
        name: s.name
      }));
      setStateOptions(formattedStates);
    } else {
      setStateOptions([]);
    }
  }, [selectedLocation.country]);

  useEffect(() => {
    if (selectedLocation.country) {
      if (selectedLocation.state) {
        const cities = City.getCitiesOfState(selectedLocation.country.isoCode, selectedLocation.state.isoCode);
        const formattedCities = cities.map(c => ({
          ...c,
          id: `${c.name}-${c.stateCode}`,
          name: c.name
        }));
        setCityOptions(formattedCities);
      } else {
        const cities = City.getCitiesOfCountry(selectedLocation.country.isoCode);
        const formattedCities = cities.map(c => ({
          ...c,
          id: `${c.name}-${c.stateCode}`,
          name: c.name
        }));
        setCityOptions(formattedCities);
      }
    } else {
      setCityOptions([]);
    }
  }, [selectedLocation.country, selectedLocation.state]);

  const handleCountrySelect = (value) => {
    setSelectedLocation({
      country: value,
      state: null,
      city: null
    });
    setFormData(prev => ({
      ...prev,
      country: value ? value.name : '',
      state: '',
      city: ''
    }));
  };

  const handleStateSelect = (value) => {
    let countryUpdate = {};
    if (value && value.countryCode && (!selectedLocation.country || selectedLocation.country.isoCode !== value.countryCode)) {
      const country = Country.getCountryByCode(value.countryCode);
      if (country) {
        const countryObj = { ...country, id: country.isoCode, name: country.name };
        countryUpdate = { country: countryObj };
      }
    }

    setSelectedLocation(prev => ({
      ...prev,
      ...countryUpdate,
      state: value,
      city: null
    }));

    setFormData(prev => ({
      ...prev,
      ...(countryUpdate.country ? { country: countryUpdate.country.name } : {}),
      state: value ? value.name : '',
      city: '',
      region: mapRegionToDropdown(value ? value.name : '')
    }));
  };

  const handleCitySelect = (value) => {
    let autoUpdates = {};

    if (value && value.countryCode && value.stateCode) {
      // Auto-fill country if missing or different
      if (!selectedLocation.country || selectedLocation.country.isoCode !== value.countryCode) {
        const country = Country.getCountryByCode(value.countryCode);
        if (country) {
          autoUpdates.country = { ...country, id: country.isoCode, name: country.name };
        }
      }

      // Auto-fill state if missing or different
      if (!selectedLocation.state || selectedLocation.state.isoCode !== value.stateCode) {
        const state = State.getStateByCodeAndCountry(value.stateCode, value.countryCode);
        if (state) {
          autoUpdates.state = { ...state, id: state.isoCode, name: state.name };
        }
      }
    }

    setSelectedLocation(prev => ({
      ...prev,
      ...autoUpdates,
      city: value
    }));

    setFormData(prev => ({
      ...prev,
      ...(autoUpdates.country ? { country: autoUpdates.country.name } : {}),
      ...(autoUpdates.state ? { state: autoUpdates.state.name } : {}),
      city: value ? value.name : '',
      ...(autoUpdates.state ? { region: mapRegionToDropdown(autoUpdates.state.name) } : {})
    }));
  };

  // Auto-fill address based on pincode
  useEffect(() => {
    const pincode = formData.pincode;
    const countryObj = selectedLocation.country;

    // Trigger if pincode is long enough (4+ for numeric, 3+ for alphanumeric)
    const isAlpha = /[A-Z]/i.test(pincode || '');
    const minLength = isAlpha ? 3 : 4;

    if (pincode && pincode.length >= minLength) {
      const lookupPincode = async () => {
        const searchKey = `${pincode}-${countryObj?.isoCode || ''}`;
        if (searchKey === lastSearchedKeyRef.current) return;

        setIsPincodeLoading(true);
        lastSearchedKeyRef.current = searchKey;

        try {
          const result = await fetchPincodeDetails(pincode, countryObj?.isoCode || '');
          if (result.success) {
            // 1. Resolve Country (Always try to resolve from result to allow overrides)
            let countryToUse = countryOptions.find(c =>
              c.isoCode === result.countryCode ||
              c.name.toLowerCase() === (result.country || '').toLowerCase()
            );

            if (!countryToUse) return;

            // 2. Resolve State
            const states = State.getStatesOfCountry(countryToUse.isoCode);
            const foundState = states.find(s =>
              s.name.toLowerCase() === (result.state || '').toLowerCase() ||
              s.isoCode.toLowerCase() === (result.state || '').toLowerCase() ||
              (result.state || '').toLowerCase().includes(s.name.toLowerCase())
            );

            if (foundState) {
              const stateObj = { ...foundState, id: foundState.isoCode, name: foundState.name };

              // 3. Resolve City (with fuzzy matching)
              const cities = City.getCitiesOfState(countryToUse.isoCode, foundState.isoCode);
              const foundCity = cities.find(c =>
                c.name.toLowerCase() === (result.city || '').toLowerCase() ||
                (result.city || '').toLowerCase() === c.name.toLowerCase() ||
                (result.city || '').toLowerCase().includes(c.name.toLowerCase())
              );

              const cityObj = foundCity
                ? { ...foundCity, id: `${foundCity.name}-${foundCity.stateCode}`, name: foundCity.name }
                : { id: result.city, name: result.city };

              // Only update and toast if the location is actually different
              const isDifferent =
                countryToUse.isoCode !== countryObj?.isoCode ||
                stateObj.name !== selectedLocation.state?.name ||
                cityObj.name !== selectedLocation.city?.name;

              if (isDifferent) {
                setSelectedLocation({
                  country: countryToUse,
                  state: stateObj,
                  city: cityObj
                });

                setFormData(prev => ({
                  ...prev,
                  country: countryToUse.name,
                  state: stateObj.name,
                  city: cityObj.name,
                  region: mapRegionToDropdown(stateObj.name)
                }));

                toast.success(`Location auto-filled for ${pincode} (${countryToUse.name})`);

                // Update search key to include the newly set country to prevent re-trigger
                lastSearchedKeyRef.current = `${pincode}-${countryToUse.isoCode}`;
              }
            } else {
              toast.error(`State not found for ${pincode}`);
            }
          } else {
            // Only show error if the code looks complete (e.g. 5-6 digits or UK format)
            if (pincode.length >= 5) {
              toast.error(`Location not found for pincode: ${pincode}`);
            }
          }
        } catch (error) {
          // Removed log
        } finally {
          setIsPincodeLoading(false);
        }
      };

      const timer = setTimeout(() => {
        lookupPincode();
      }, 600); // Slightly longer debounce for international lookups
      return () => clearTimeout(timer);
    }
  }, [formData.pincode, selectedLocation.country, countryOptions]);

  // No longer need manual useEffect for initial fetches as useQuery handles it
  // Debounced search for users is still useful to update userSearchTerm
  useEffect(() => {
    if (!isUserDropdownOpen) return;
    const handler = setTimeout(() => {
      // queryClient will refetch as userSearchTerm changes
    }, 400);
    return () => clearTimeout(handler);
  }, [userSearchTerm, isUserDropdownOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Map backend source to frontend display format
  const mapSourceToFrontend = (source) => {
    // First, check if it's an ID from masterSources
    const masterSource = masterSources.find(s => s._id === source || s.id === source);
    if (masterSource) return masterSource.name;

    // Then, map hardcoded values
    const sourceMap = {
      'website': 'Website',
      'facebook': 'Social Media',
      'instagram': 'Social Media',
      'linkedin': 'LinkedIn',
      'google_ads': 'Google Ads',
      'whatsapp': 'WhatsApp',
      'referral': 'Referral',
      'cold_call': 'Cold Call',
      'walk_in': 'Walk In',
      'other': 'Manual Entry'
    };
    return sourceMap[source] || 'Manual Entry';
  };



  // Map employee count to company size string
  const mapEmployeeCountToCompanySize = (employeeCount) => {
    if (!employeeCount) return '';
    if (employeeCount <= 10) return 'Startup (1-10 employees)';
    if (employeeCount <= 50) return 'Small (10-50 employees)';
    if (employeeCount <= 200) return 'Medium (50-200 employees)';
    if (employeeCount <= 1000) return 'Large (200-1000 employees)';
    return 'Enterprise (1000+ employees)';
  };

  // Map region value to match dropdown options
  const mapRegionToDropdown = (regionValue) => {
    if (!regionValue) return '';
    // If it's already one of the dropdown options, return it
    if (regions.includes(regionValue)) return regionValue;
    // Map state names to regions (if state is provided instead of region)
    const stateToRegionMap = {
      'Jammu and Kashmir': 'North',
      'Himachal Pradesh': 'North',
      'Punjab': 'North',
      'Uttarakhand': 'North',
      'Haryana': 'North',
      'Delhi': 'North',
      'Rajasthan': 'North',
      'Uttar Pradesh': 'North',
      'Karnataka': 'South',
      'Kerala': 'South',
      'Tamil Nadu': 'South',
      'Andhra Pradesh': 'South',
      'Telangana': 'South',
      'Puducherry': 'South',
      'Goa': 'West',
      'Gujarat': 'West',
      'Maharashtra': 'West',
      'Madhya Pradesh': 'Central',
      'Chhattisgarh': 'Central',
      'Bihar': 'East',
      'Jharkhand': 'East',
      'Odisha': 'East',
      'West Bengal': 'East',
      'Assam': 'North-East',
      'Sikkim': 'North-East',
      'Arunachal Pradesh': 'North-East',
      'Manipur': 'North-East',
      'Meghalaya': 'North-East',
      'Mizoram': 'North-East',
      'Nagaland': 'North-East',
      'Tripura': 'North-East'
    };
    return stateToRegionMap[regionValue] || '';
  };

  // Map timeline value to match dropdown options
  const mapTimelineToDropdown = (timelineValue) => {
    if (!timelineValue) return '';
    // If it's already one of the dropdown options, return it
    if (timelines.includes(timelineValue)) return timelineValue;
    // Map common timeline formats to dropdown options
    const timelineMap = {
      'immediate': 'Immediate (0-30 days)',
      '0-30 days': 'Immediate (0-30 days)',
      'short-term': 'Short-term (1-3 months)',
      '1-3 months': 'Short-term (1-3 months)',
      'medium-term': 'Medium-term (3-6 months)',
      '3-6 months': 'Medium-term (3-6 months)',
      'long-term': 'Long-term (6+ months)',
      '6+ months': 'Long-term (6+ months)'
    };
    return timelineMap[timelineValue.toLowerCase()] || '';
  };



  // Load lead data when in edit mode
  // Removed manual loadLeadData as it's replaced by useQuery above

  // Required fields - only personal information is required
  const requiredFields = ['firstName', 'phone'];

  const validateFormData = () => {
    const newErrors = {};

    // Validate required fields
    requiredFields.forEach(field => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
        newErrors[field] = `${fieldLabel} is required.`;
      }
    });

    // Validate email format
    if (formData.email && !formData.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Validate phone number (must be 10 digits and start with 6-9)
    if (formData.phone) {
      const phoneValidation = validatePhoneInput(formData.phone);
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error ? (phoneValidation.error.endsWith('.') ? phoneValidation.error : `${phoneValidation.error}.`) : 'Phone number must be 10-15 digits.';
      } else if (!/^\+?\d{10,15}$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number (10-15 digits, optional + at start).';
      }
    }

    // Validate alternate phone if provided
    if (formData.alternatePhone) {
      const altPhoneValidation = validatePhoneInput(formData.alternatePhone);
      if (!altPhoneValidation.isValid) {
        newErrors.alternatePhone = altPhoneValidation.error ? (altPhoneValidation.error.endsWith('.') ? altPhoneValidation.error : `${altPhoneValidation.error}.`) : 'Phone number must be 10-15 digits.';
      } else if (!/^\+?\d{10,15}$/.test(formData.alternatePhone)) {
        newErrors.alternatePhone = 'Please enter a valid phone number (10-15 digits, optional + at start).';
      }
    }

    // Validate other numeric fields
    const numericFields = ['annualRevenue', 'leadValue', 'budget'];
    numericFields.forEach(field => {
      const value = formData[field];
      if (value && value !== '') {
        const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
        const minValue = NUMBER_LIMITS[field]?.min || 0;
        const numValidation = validateNumberInput(value, maxValue, minValue);
        if (!numValidation.isValid) {
          newErrors[field] = numValidation.error ? (numValidation.error.endsWith('.') ? numValidation.error : `${numValidation.error}.`) : 'Please enter a valid number.';
        }
      }
    });

    // Validate postal code (optional field)
    if (formData.pincode && formData.pincode.trim() !== '') {
      if (formData.pincode.length > 15) {
        newErrors.pincode = 'Postal Code cannot exceed 15 characters.';
      } else if (!/^[a-zA-Z0-9\s]+$/.test(formData.pincode.trim())) {
        newErrors.pincode = 'Postal Code must contain only letters and numbers.';
      }
    }

    // Validate lastName if provided (optional field)
    if (formData.lastName && formData.lastName.trim() !== '') {
      const lastNamePattern = /^[a-zA-Z\s]{0,50}$/;
      if (!lastNamePattern.test(formData.lastName.trim())) {
        newErrors.lastName = 'Last name must contain only letters and spaces (max 50 characters).';
      }
    }

    // Validate documents
    if (formData.companyDocuments && formData.companyDocuments.length > 0) {
      formData.companyDocuments.forEach((doc, idx) => {
        if (doc.certificateNumber && doc.certificateNumber.trim().length > 20) {
          newErrors[`companyDocuments_${idx}_cert`] = 'Certificate Number cannot exceed 20 characters.';
        }
        if ((doc.fileType || 'document') === 'video' && !doc.verificationAssignedTo) {
          newErrors[`companyDocuments_${idx}_verifier`] = 'Decision maker is required for video verification.';
        }
      });
    }

    if (isStatusChangedInEdit) {
      const reason = (formData.statusChangeReason || '').trim();
      if (!reason) {
        newErrors.statusChangeReason = 'Status change reason is required.';
      } else if (reason.length > 1000) {
        newErrors.statusChangeReason = 'Status change reason cannot exceed 1000 characters.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handler for formatted number inputs (with comma separators)
  const handleFormattedNumberChange = (field, value) => {
    // Remove all non-digit characters
    const numericValue = parseFormattedNumber(value);

    // Validate the numeric value
    const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
    const minValue = NUMBER_LIMITS[field]?.min || 0;

    let processedValue = numericValue;
    let error = '';

    if (numericValue === '') {
      // Allow empty value
      processedValue = '';
    } else {
      const numValue = parseFloat(numericValue);
      if (isNaN(numValue)) {
        processedValue = '';
        error = 'Please enter a valid number.';
      } else if (numValue < minValue) {
        processedValue = String(minValue);
        error = `Value must be at least ${minValue}.`;
      } else if (numValue > maxValue) {
        processedValue = String(maxValue);
        error = `Value cannot exceed ₹${formatIndianAmount(maxValue)}.`;
      } else {
        processedValue = numericValue;
      }
    }

    // Store the numeric value (without commas) in formData
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Set error if validation failed
    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (errors[field]) {
      // Clear error if validation passed
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;
    let error = '';

    if (fieldType === 'select') {
      // For dropdowns/select, use value directly without sanitization
      processedValue = value || '';
    } else if (field === 'pincode') {
      // For Postal Code (originally pincode), allow alphanumeric characters, up to 15
      processedValue = String(value || '').replace(/[^a-zA-Z0-9\s]/g, '').slice(0, 15);
      if (processedValue.length > 15) {
        error = 'Postal Code cannot exceed 15 characters.';
      }
    } else if (fieldType === 'text') {
      // Sanitize text input
      processedValue = sanitizeTextInput(String(value || ''));
      // Limit length
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'number') {
      // Validate number input
      const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
      const minValue = NUMBER_LIMITS[field]?.min || 0;
      const numValidation = validateNumberInput(value, maxValue, minValue);
      processedValue = numValidation.value;
      error = numValidation.error ? (numValidation.error.endsWith('.') ? numValidation.error : `${numValidation.error}.`) : '';
    } else if (fieldType === 'phone') {
      // Validate phone input
      const phoneValidation = validatePhoneInput(value);
      processedValue = phoneValidation.value;
      const baseError = phoneValidation.error;

      if (baseError) {
        error = baseError.endsWith('.') ? baseError : `${baseError}.`;
      } else if (processedValue && !/^\d{10,15}$/.test(processedValue)) {
        error = 'Please enter a valid phone number (10-15 digits).';
      }
    } else if (fieldType === 'email') {
      // For email, allow email characters including + (plus sign is valid in emails)
      // Use sanitizeEmailInput which allows @ . - _ +
      processedValue = sanitizeEmailInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);

      if (processedValue && !processedValue.includes('@')) {
        error = 'Please enter a valid email address.';
      }
    } else if (fieldType === 'url') {
      // For URL, allow URL characters
      processedValue = String(value || '');
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'textarea') {
      // For textarea, sanitize text but allow more characters
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'description') {
      // For description/notes, allow commas, periods, and other punctuation
      processedValue = sanitizeDescriptionInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'address') {
      // For address, allow ( ) - , . characters
      processedValue = sanitizeAddressInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    }

    // Auto-transform to uppercase and enforce character sets for specific fields (removed gst and drugLicense)
    // No specific uppercase validations currently for non-array fields

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Set error if validation failed
    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (errors[field]) {
      // Clear error if validation passed
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBlur = (field) => {
    const value = formData[field];
    let error = '';

    // Validate required fields
    if (requiredFields.includes(field) && (!value || (typeof value === 'string' && value.trim() === ''))) {
      const fieldLabel = field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').trim();
      error = `${fieldLabel} is required.`;
    } else {
      // Field-specific validation
      if (field === 'email' && value && !value.includes('@')) {
        error = 'Please enter a valid email address.';
      } else if ((field === 'phone' || field === 'alternatePhone') && value) {
        const phoneValidation = validatePhoneInput(value);
        if (!phoneValidation.isValid) {
          error = phoneValidation.error ? (phoneValidation.error.endsWith('.') ? phoneValidation.error : `${phoneValidation.error}.`) : 'Phone number must be 10-15 digits.';
        } else if (!/^\+?\d{10,15}$/.test(value)) {
          error = 'Please enter a valid phone number (10-15 digits, optional + at start).';
        }
      } else if (field === 'pincode' && value) {
        if (String(value).length > 15) {
          error = 'Postal Code cannot exceed 15 characters.';
        } else if (!/^[a-zA-Z0-9\s]+$/.test(String(value).trim())) {
          error = 'Postal Code must contain only letters and numbers.';
        }
      } else if (['annualRevenue', 'leadValue', 'budget'].includes(field) && value) {
        const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
        const minValue = NUMBER_LIMITS[field]?.min || 0;
        const numValidation = validateNumberInput(value, maxValue, minValue);
        if (!numValidation.isValid) {
          error = numValidation.error ? (numValidation.error.endsWith('.') ? numValidation.error : `${numValidation.error}.`) : 'Please enter a valid number.';
        }
      }
    }

    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const handleTagAdd = (tag) => {
    const currentTags = Array.isArray(formData.tags) ? formData.tags : [];
    const trimmedTag = tag.trim();

    // Validate tag length (max 50 characters)
    if (trimmedTag.length > 50) {
      setErrors(prev => ({
        ...prev,
        tags: 'Each tag must be 50 characters or less.'
      }));
      return;
    }

    // Clear tag error if validation passes
    if (errors.tags) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.tags;
        return newErrors;
      });
    }

    if (trimmedTag && !currentTags.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...currentTags, trimmedTag]
      }));
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: Array.isArray(prev.tags) ? prev.tags.filter(tag => tag !== tagToRemove) : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    if (!validateFormData()) {
      const missingFields = requiredFields.filter(field =>
        !formData[field] || (typeof formData[field] === 'string' && formData[field].trim() === '')
      );

      if (missingFields.length > 0) {
        // Show individual field error messages instead of combined alert
        const fieldLabels = missingFields.map(f =>
          f.charAt(0).toUpperCase() + f.slice(1).replace(/([A-Z])/g, ' $1').trim()
        );

        // Set focus to first missing field
        const firstMissingField = missingFields[0];
        const fieldElements = document.getElementsByName(firstMissingField);
        if (fieldElements.length > 0) {
          fieldElements[0].focus();
        }

        // Don't show alert, let the form validation errors display naturally
        return;
      } else {
        // Show first validation error by focusing on the field
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField && errors[firstErrorField]) {
          // Focus on the first field with an error
          const fieldElements = document.getElementsByName(firstErrorField);
          if (fieldElements.length > 0) {
            fieldElements[0].focus();
          }
        }
      }
      return;
    }

    // Ensure all form data is properly formatted, preserving arrays for certain fields
    const formattedFormData = Object.keys(formData).reduce((acc, key) => {
      const value = formData[key];

      // Send status change reason only when status actually changes in edit mode
      if (key === 'statusChangeReason' && !isStatusChangedInEdit) {
        return acc;
      }

      // Skip owner field (it's just for display), use assignedTo instead
      if (key === 'owner') {
        return acc;
      }

      // Preserve arrays for tags field
      if (key === 'tags' && Array.isArray(value)) {
        acc[key] = value;
      } else if (key === 'assignedTo' || key === 'industry' || key === 'decisionMaker' || key === 'source' || key === 'category' || key === 'leadVideoStatus' || key === 'leadOrderStatus' || key === 'leadCallStatus' || key === 'leadInterest') {
        // ID fields should be null if empty to allow clearing in backend
        acc[key] = value && value !== '' ? value : null;
      } else if (key === 'companyDocuments') {
        // Clean up companyDocuments: remove docs with no method/content and fix empty IDs
        acc[key] = (value || []).map(doc => {
          const docCopy = { ...doc };
          if (docCopy.documentTypeId === '') delete docCopy.documentTypeId;
          if (docCopy.verificationAssignedTo === '') delete docCopy.verificationAssignedTo;
          delete docCopy.verificationAssignedToName;
          delete docCopy.verificationAssignedToEmail;
          if (docCopy.certificateNumber) docCopy.certificateNumber = docCopy.certificateNumber.trim();
          docCopy.fileType = docCopy.fileType || 'document';
          if (docCopy.fileType === 'video') {
            docCopy.certificateNumber = '';
            docCopy.expiryDate = '';
            if (docCopy.documentMethod === 'link') {
              docCopy.videoUrl = docCopy.documentLink;
            }
          }
          return docCopy;
        }).filter(doc => {
          const hasType = !!(doc.documentTypeId || doc.documentTypeName || doc.documentType);
          const hasVideoContent = doc.fileType === 'video' && ((doc.documentMethod === 'upload' && doc.videoUrl) || (doc.documentMethod === 'link' && doc.documentLink));
          const hasDocumentContent = doc.fileType !== 'video' && ((doc.certificateNumber && doc.certificateNumber.trim()) || (doc.documentMethod === 'upload' && doc.companyPdf) || (doc.documentMethod === 'link' && doc.documentLink));
          const hasContent = hasVideoContent || hasDocumentContent;
          return hasType || hasContent;
        });
      } else if (key === 'statusChangeReason') {
        acc[key] = String(value || '').trim().slice(0, 1000);
      } else {
        // Handle objects specifically to extract ID if they somehow slipped in
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          acc[key] = value._id || value.id || String(value);
        } else {
          acc[key] = value !== null && value !== undefined ? String(value) : '';
        }
      }
      return acc;
    }, {});

    // Auto-assign lead to logged-in sales executive on create
    if (!isEditMode && isSalesExecutive && user) {
      const currentUserId = user.id || user.user || user._id;
      if (currentUserId) {
        formattedFormData.assignedTo = currentUserId;
      }
    }

    // Add nested geography data for the new backend structure
    const finalPayload = {
      ...formattedFormData,
      countryObj: selectedLocation.country,
      stateObj: selectedLocation.state,
      cityObj: selectedLocation.city,
      postalCode: formData.pincode // Pass as postalCode for consistency with new schema
    };

    // Submit lead mutation
    leadMutation.mutate(finalPayload);
  };

  const handleCancel = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Helper function to check if a tab has errors
  const getTabErrors = (tabKey) => {
    const tabFields = {
      personal: ['firstName', 'lastName', 'email', 'phone'],
      company: ['company', 'industry', 'website'],
      address: ['city', 'state', 'pincode'],
      lead: ['region', 'category'],
      additional: []
    };

    return tabFields[tabKey]?.some(field => errors[field]) || false;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        {isEditMode && (
          <button
            type="button"
            onClick={handleCancel}
            className="mr-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-6 w-6 text-gray-500" />
          </button>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Lead' : 'Manual Lead Entry'}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? 'Update lead information' : 'Create a new lead manually'}
          </p>
        </div>
      </div>

      {/* Loading state while fetching lead data */}
      {isLoadingLead && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading lead data...</p>
          </div>
        </div>
      )}

      {/* Form */}
      {!isLoadingLead && (
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                {[
                  { key: 'personal', label: 'Personal Information', icon: User },
                  { key: 'company', label: 'Company Information', icon: Building },
                  { key: 'address', label: 'Address Information', icon: MapPin },
                  { key: 'lead', label: 'Lead Information', icon: Tag },
                  { key: 'additional', label: 'Additional Information', icon: FileText }
                ].map((tab) => {
                  const IconComponent = tab.icon;
                  const hasErrors = getTabErrors(tab.key);
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.key
                        ? 'border-agrosaf-green text-agrosaf-green'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      <IconComponent className="h-4 w-4" />
                      <span>{tab.label}</span>
                      {hasErrors && (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'personal' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value, 'text', FIELD_LIMITS.firstName)}
                        onBlur={() => handleBlur('firstName')}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter first name"
                        aria-invalid={!!errors.firstName}
                        aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.firstName && (
                          <p id="firstName-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.firstName}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.firstName, FIELD_LIMITS.firstName)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value, 'text', FIELD_LIMITS.lastName)}
                        onBlur={() => handleBlur('lastName')}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter last name"
                        aria-invalid={!!errors.lastName}
                        aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.lastName && (
                          <p id="lastName-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.lastName}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.lastName, FIELD_LIMITS.lastName)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value, 'email', 100)}
                        onBlur={() => handleBlur('email')}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter email address"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.email && (
                          <p id="email-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.email}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.email, 100)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value, 'phone')}
                        onBlur={() => handleBlur('phone')}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.phone ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter phone number (e.g. +919876543210)"
                        maxLength={15}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? 'phone-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.phone && (
                          <p id="phone-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.phone}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.phone, FIELD_LIMITS.phone)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Alternate Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.alternatePhone}
                        onChange={(e) => handleInputChange('alternatePhone', e.target.value, 'phone')}
                        onBlur={() => handleBlur('alternatePhone')}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.alternatePhone ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter phone number (optional)"
                        maxLength={15}
                        aria-invalid={!!errors.alternatePhone}
                        aria-describedby={errors.alternatePhone ? 'alternatePhone-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.alternatePhone && (
                          <p id="alternatePhone-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.alternatePhone}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.alternatePhone, FIELD_LIMITS.phone)}
                        </span>
                      </div>
                    </div>

                    {!isSalesExecutive && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assign To
                        </label>
                        <div className="relative" ref={userDropdownRef}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsUserDropdownOpen(!isUserDropdownOpen);
                            }}
                            disabled={isLoadingUsers}
                            className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${isLoadingUsers ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                              }`}
                          >
                            <span className="truncate">
                              {formData.assignedTo
                                ? (() => {
                                  const selectedUser = (userData || []).find(u => u.id === formData.assignedTo);
                                  return selectedUser
                                    ? `${selectedUser.name} ${selectedUser.email ? `(${selectedUser.email})` : ''}`
                                    : (formData.owner || 'Unknown User');
                                })()
                                : 'Unassigned'}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {/* Dropdown */}
                          {isUserDropdownOpen && (
                            <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                              <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    value={userSearchTerm}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                      setUserSearchTerm(value);
                                    }}
                                    placeholder="Search users..."
                                    maxLength={100}
                                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                    autoFocus
                                  />
                                </div>
                              </div>
                              <div className="overflow-y-auto max-h-[240px]">
                                {isLoadingUsers && filteredUsers.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    Loading users...
                                  </div>
                                ) : filteredUsers.length === 0 ? (
                                  <div className="p-3 text-sm text-gray-500 text-center">
                                    {userSearchTerm ? 'No users found' : 'No users available'}
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleInputChange('owner', '', 'select');
                                        handleInputChange('assignedTo', '', 'select');
                                        setIsUserDropdownOpen(false);
                                        setUserSearchTerm('');
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 ${!formData.assignedTo ? 'bg-gray-100 font-medium' : 'text-gray-700'
                                        }`}
                                    >
                                      Unassigned
                                    </button>
                                    {filteredUsers.map(user => (
                                      <button
                                        key={user.id}
                                        type="button"
                                        onClick={() => {
                                          handleInputChange('owner', user.name, 'select');
                                          handleInputChange('assignedTo', user.id, 'select');
                                          setIsUserDropdownOpen(false);
                                          setUserSearchTerm('');
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${formData.assignedTo === user.id ? 'bg-gray-100 font-medium' : 'text-gray-700'
                                          }`}
                                      >
                                        <div className="truncate font-medium">{user.name}</div>
                                        {user.email && (
                                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                                        )}
                                        {user.designation && (
                                          <div className="text-xs text-gray-500 truncate capitalize">{user.designation}</div>
                                        )}
                                      </button>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {isLoadingUsers && !isUserDropdownOpen && (
                          <p className="mt-1 text-xs text-gray-500">Loading users...</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'company' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value, 'text', FIELD_LIMITS.company)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.company ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter company name"
                        aria-invalid={!!errors.company}
                        aria-describedby={errors.company ? 'company-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.company && (
                          <p id="company-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.company}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.company, FIELD_LIMITS.company)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleInputChange('website', e.target.value, 'url', 200)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.website ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        placeholder="https://www.company.com"
                        aria-invalid={!!errors.website}
                        aria-describedby={errors.website ? 'website-error' : undefined}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {errors.website && (
                          <p id="website-error" className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.website}
                          </p>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.website, 200)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => handleInputChange('industry', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.industry ? 'border-red-500' : 'border-gray-300'
                          } ${isLoadingMasters ? 'bg-gray-50' : ''}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Industries...' : 'Select Industry'}</option>
                        {masterIndustries.map((industry) => (
                          <option key={industry._id} value={industry._id}>{industry.name}</option>
                        ))}
                        {/* Show lead's existing industry if it's inactive and not in the main list */}
                        {isEditMode && formData.industry && leadIndustryInfo && !masterIndustries.find(i => i._id === formData.industry) && (
                          <option value={leadIndustryInfo._id}>
                            {leadIndustryInfo.name} (Inactive)
                          </option>
                        )}
                      </select>
                      {!isLoadingMasters && masterIndustries.length === 0 && !formData.industry && (
                        <p className="mt-1 text-xs text-amber-600">No active industries found in configuration.</p>
                      )}
                      {errors.industry && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.industry}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <select
                        value={formData.companySize}
                        onChange={(e) => handleInputChange('companySize', e.target.value, 'select')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="">Select Company Size</option>
                        {masterCompanySizes.map((size) => (
                          <option key={size._id} value={size.companySizeCode || size._id} className='capitalize'>{size.name}</option>
                        ))}
                        {formData.companySize && !masterCompanySizes.some(s => (s.companySizeCode || s._id) === formData.companySize) && (
                          <option value={formData.companySize} className='capitalize'>{formData.companySize}</option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Annual Revenue
                      </label>
                      <input
                        type="number"
                        value={formData.annualRevenue}
                        onChange={(e) => handleInputChange('annualRevenue', e.target.value, 'number')}
                        min="0"
                        max={NUMBER_LIMITS.annualRevenue.max}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        placeholder="Enter annual revenue"
                        onBlur={() => handleBlur('annualRevenue')}
                        aria-invalid={!!errors.annualRevenue}
                        aria-describedby={errors.annualRevenue ? 'annualRevenue-error' : undefined}
                      />
                      {errors.annualRevenue && (
                        <p id="annualRevenue-error" className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.annualRevenue}
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2 space-y-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Company Documents
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addDocument}
                          className="bg-gray-50 hover:bg-gray-100"
                          size="sm"
                        >
                          + Add Document
                        </Button>
                      </div>

                      {formData.companyDocuments.length === 0 && (
                        <div className="p-4 border border-dashed border-gray-300 rounded-lg text-center text-gray-500 text-sm">
                          No documents added. Click "+ Add Document" to upload or link a document.
                        </div>
                      )}

                      {formData.companyDocuments.map((doc, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            disabled={isDocumentUploading(index) || isDocumentDeleting(index)}
                            className="absolute top-4 right-4 text-red-500 hover:text-red-700 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove Document"
                          >
                            <X className="h-5 w-5" />
                          </button>
                          <div className={`grid grid-cols-1 ${(doc.fileType || 'document') === 'video' ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 mt-2`}>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                File Type
                              </label>
                              <select
                                value={doc.fileType || 'document'}
                                onChange={(e) => handleDocumentChange(index, 'fileType', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                              >
                                <option value="document">Document</option>
                                <option value="video">Video</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Document Type
                              </label>
                              <select
                                value={doc.documentTypeId || doc.documentType || ''}
                                onChange={(e) => {
                                  const selectedId = e.target.value;
                                  const selectedDocType = masterLeadDocumentTypes.find(dt => dt._id === selectedId);

                                  setFormData(prev => {
                                    const newDocs = [...(prev.companyDocuments || [])];
                                    if (newDocs[index]) {
                                      if (selectedDocType) {
                                        newDocs[index] = {
                                          ...newDocs[index],
                                          documentTypeId: selectedDocType._id,
                                          documentTypeName: selectedDocType.name,
                                          documentType: selectedDocType.name
                                        };
                                      } else {
                                        newDocs[index] = {
                                          ...newDocs[index],
                                          documentTypeId: selectedId,
                                          documentTypeName: selectedId,
                                          documentType: selectedId
                                        };
                                      }
                                    }
                                    return { ...prev, companyDocuments: newDocs };
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent capitalize"
                              >
                                <option value="" className="normal-case">Select Document Type</option>
                                {masterLeadDocumentTypes.map((dt) => (
                                  <option key={dt._id} value={dt._id} className="capitalize">{dt.name}</option>
                                ))}
                                {/* Render existing legacy options only if they don't match our master list */}
                                {doc.documentType && !masterLeadDocumentTypes.find(dt => dt.name === doc.documentType || dt._id === doc.documentTypeId) && (
                                  <option value={doc.documentTypeId || doc.documentType} className="capitalize">
                                    {doc.documentTypeName || doc.documentType}
                                  </option>
                                )}
                              </select>
                            </div>
                            {(doc.fileType || 'document') !== 'video' && (
                              <>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Document/Certificate Number (Optional)
                                  </label>
                                  <input
                                    type="text"
                                    value={doc.certificateNumber || ''}
                                    onChange={(e) => handleDocumentChange(index, 'certificateNumber', e.target.value.toUpperCase().slice(0, 20))}
                                    placeholder="Ex: GSTIN..."
                                    maxLength={20}
                                    className={`w-full px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors[`companyDocuments_${index}_cert`] ? 'border-red-500' : 'border-gray-300'}`}
                                  />
                                  {errors[`companyDocuments_${index}_cert`] && (
                                    <p className="mt-1 text-sm text-red-600 flex items-center">
                                      <AlertCircle className="h-4 w-4 mr-1" />
                                      {errors[`companyDocuments_${index}_cert`]}
                                    </p>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Expiry Date (Optional)
                                  </label>
                                  <input
                                    type="date"
                                    value={doc.expiryDate ? (typeof doc.expiryDate === 'string' ? doc.expiryDate.split('T')[0] : new Date(doc.expiryDate).toISOString().split('T')[0]) : ''}
                                    onChange={(e) => handleDocumentChange(index, 'expiryDate', e.target.value)}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent cursor-pointer"
                                  />
                                </div>
                              </>
                            )}

                            {(doc.fileType || 'document') === 'video' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Decision Maker <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenVerifierDropdownIndex(openVerifierDropdownIndex === index ? null : index);
                                    }}
                                    disabled={isLoadingSuperAdmins && openVerifierDropdownIndex === index}
                                    className={`w-full px-3 py-2 border rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors[`companyDocuments_${index}_verifier`] ? 'border-red-500' : 'border-gray-300'}`}
                                  >
                                    <span className="truncate">
                                      {getDocumentVerifierLabel(doc)}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openVerifierDropdownIndex === index ? 'rotate-180' : ''}`} />
                                  </button>

                                  {openVerifierDropdownIndex === index && (
                                    <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                                      <div className="p-2 border-b border-gray-100">
                                        <div className="relative">
                                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                          <input
                                            type="text"
                                            value={verifierSearchTerms[index] || ''}
                                            onChange={(e) => {
                                              const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                              setVerifierSearchTerms(prev => ({ ...prev, [index]: value }));
                                            }}
                                            placeholder="Search super admins..."
                                            maxLength={100}
                                            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                            autoFocus
                                          />
                                        </div>
                                      </div>
                                      <div className="overflow-y-auto max-h-[240px]">
                                        {isLoadingSuperAdmins && filteredSuperAdmins.length === 0 ? (
                                          <div className="p-3 text-sm text-gray-500 text-center">
                                            Loading decision makers...
                                          </div>
                                        ) : filteredSuperAdmins.length === 0 ? (
                                          <div className="p-3 text-sm text-gray-500 text-center">
                                            {verifierSearchTerms[index] ? 'No super admins found' : 'No super admins available'}
                                          </div>
                                        ) : (
                                          filteredSuperAdmins.map(admin => (
                                            <button
                                              key={admin.id}
                                              type="button"
                                              onClick={() => handleVerifierSelect(index, admin)}
                                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${String(doc.verificationAssignedTo) === String(admin.id) ? 'bg-gray-100 font-medium' : 'text-gray-700'}`}
                                            >
                                              <div className="truncate font-medium">{admin.name}</div>
                                              {admin.email && (
                                                <div className="text-xs text-gray-500 truncate">{admin.email}</div>
                                              )}
                                              <div className="text-xs text-gray-500 truncate capitalize">{admin.designation || 'Super Admin'}</div>
                                            </button>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {errors[`companyDocuments_${index}_verifier`] && (
                                  <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {errors[`companyDocuments_${index}_verifier`]}
                                  </p>
                                )}
                              </div>
                            )}

                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {(doc.fileType || 'document') === 'video' ? 'Video Input Method' : 'Document Input Method'}
                              </label>
                              <div className="flex items-center space-x-6 h-[42px]">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`documentMethod_${index}`}
                                    value="upload"
                                    checked={doc.documentMethod === 'upload'}
                                    onChange={(e) => handleDocumentChange(index, 'documentMethod', e.target.value)}
                                    className="w-4 h-4 text-agrosaf-green focus:ring-agrosaf-green"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">{(doc.fileType || 'document') === 'video' ? 'Upload Video' : 'Upload Document'}</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`documentMethod_${index}`}
                                    value="link"
                                    checked={doc.documentMethod === 'link'}
                                    onChange={(e) => handleDocumentChange(index, 'documentMethod', e.target.value)}
                                    className="w-4 h-4 text-agrosaf-green focus:ring-agrosaf-green"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">{(doc.fileType || 'document') === 'video' ? 'Video Link' : 'Document Link'}</span>
                                </label>
                              </div>
                            </div>
                          </div>

                          {doc.documentMethod === 'link' ? (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {(doc.fileType || 'document') === 'video' ? 'Video Link URL' : 'Document Link URL'}
                              </label>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="url"
                                  value={doc.documentLink}
                                  onChange={(e) => handleDocumentChange(index, 'documentLink', e.target.value.slice(0, 500))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                  placeholder={(doc.fileType || 'document') === 'video' ? 'https://example.com/video' : 'https://example.com/document.pdf'}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!doc.documentLink) {
                                      toast.error('Please enter a link to verify.');
                                      return;
                                    }
                                    try {
                                      // Validate URL format first
                                      new URL(doc.documentLink);
                                      // Open in new tab
                                      window.open(doc.documentLink, '_blank', 'noopener,noreferrer');
                                    } catch (error) {
                                      toast.error('Invalid link format. Please check the URL.');
                                    }
                                  }}
                                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 whitespace-nowrap transition-colors shadow-sm"
                                >
                                  Verify Link
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {(doc.fileType || 'document') === 'video' ? 'Upload Video (Max 100MB)' : 'Upload Document (Max 100MB)'}
                              </label>
                              <div className="flex-1">
                                {((doc.fileType || 'document') === 'video' ? doc.videoUrl : doc.companyPdf) ? (
                                  <div className="flex items-center justify-between p-3 border border-agrosaf-green bg-green-50 rounded-lg">
                                    <div className="flex items-center space-x-3 overflow-hidden">
                                      {(doc.fileType || 'document') === 'video' ? (
                                        <Video className="h-5 w-5 text-agrosaf-green flex-shrink-0" />
                                      ) : (
                                        <FileText className="h-5 w-5 text-agrosaf-green flex-shrink-0" />
                                      )}
                                      <span className="text-sm text-gray-700 truncate">
                                        {(doc.fileType || 'document') === 'video'
                                          ? (doc.videoMetadata?.name || doc.videoMetadata?.originalName || doc.videoUrl?.split('/').pop() || 'Verification video')
                                          : (typeof doc.companyPdf === 'object' && doc.companyPdf !== null
                                            ? doc.companyPdf.name || doc.companyPdf.originalName || 'Document.pdf'
                                            : String(doc.companyPdf || '').split('/').pop() || 'Document.pdf')}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if ((doc.fileType || 'document') === 'video') {
                                            window.open(doc.videoUrl, '_blank', 'noopener,noreferrer');
                                          } else {
                                            handleDocumentDownload(doc);
                                          }
                                        }}
                                        className="p-1 text-blue-600 hover:bg-blue-100 rounded bg-white"
                                        title={(doc.fileType || 'document') === 'video' ? 'Open Video' : 'Download Document'}
                                      >
                                        <LinkIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handlePdfDelete(index)}
                                        disabled={isDocumentDeleting(index)}
                                        className="p-1 text-red-600 hover:bg-red-100 rounded bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Remove Document"
                                      >
                                        {isDocumentDeleting(index) ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <Trash2 className="h-4 w-4" />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <label
                                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed bg-white rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${isDocumentUploading(index) ? 'border-gray-200 bg-gray-50 cursor-not-allowed' :
                                      isDragging === index ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                                      }`}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                  >
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                      {isDocumentUploading(index) ? (
                                        <>
                                          <Loader2 className="w-8 h-8 mb-3 text-agrosaf-green animate-spin" />
                                          <p className="text-sm text-gray-500">
                                            {(doc.fileType || 'document') === 'video' ? 'Uploading Video...' : 'Uploading Document...'}
                                          </p>
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="w-8 h-8 mb-3 text-gray-400" />
                                          <p className="mb-2 text-sm text-gray-500">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                          </p>
                                          <p className="text-xs text-gray-400">{(doc.fileType || 'document') === 'video' ? 'Video files only (Max 100MB)' : 'PDF and image files (JPG, PNG) (Max 100MB)'}</p>
                                        </>
                                      )}
                                    </div>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept={(doc.fileType || 'document') === 'video' ? 'video/*' : '.pdf,.jpg,.jpeg,.png,image/jpeg,image/png'}
                                      onChange={(e) => handlePdfUpload(e, index)}
                                      disabled={isDocumentUploading(index)}
                                    />
                                  </label>
                                )}
                              </div>
                            </div>
                          )}

                        </div>
                      ))}

                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'address' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Address Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <LocationSelect
                        options={countryOptions}
                        value={selectedLocation.country}
                        onChange={handleCountrySelect}
                        onBlur={() => handleBlur('country')}
                        placeholder="Select Country"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <LocationSelect
                        options={stateOptions}
                        value={selectedLocation.state}
                        onChange={handleStateSelect}
                        onBlur={() => handleBlur('state')}
                        placeholder="Select State"
                        disabled={!selectedLocation.country}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <div className="relative">
                        <LocationSelect
                          options={cityOptions}
                          value={selectedLocation.city}
                          onChange={handleCitySelect}
                          onBlur={() => {
                            handleBlur('city');
                            // If no city is selected but there's a search term, use it as a custom city
                            // Note: This logic depends on LocationSelect passing the search term or us managing it
                          }}
                          placeholder="Select or Enter City"
                          disabled={false}
                          className="w-full"
                          searchable={true}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value, 'address', FIELD_LIMITS.address)}
                        onBlur={() => handleBlur('address')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        placeholder="Street, Building, Floor"
                      />
                      <div className="flex justify-end mt-1">
                        <p className="text-xs text-gray-500">
                          {getCharacterCount(formData.address, FIELD_LIMITS.address)}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Postal Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={(e) => handleInputChange('pincode', e.target.value)}
                          onBlur={() => handleBlur('pincode')}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.pincode ? 'border-red-500' : 'border-gray-300'
                            } ${isPincodeLoading ? 'pr-10' : ''}`}
                          placeholder="Enter postal code"
                          maxLength={15}
                        />
                        {isPincodeLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-agrosaf-green"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        {errors.pincode && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {errors.pincode}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {getCharacterCount(formData.pincode, 15)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'lead' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Lead Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Source
                      </label>
                      <select
                        value={mapLeadSourceToMasterId(formData.source, masterSources)}
                        onChange={(e) => handleInputChange('source', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.source ? 'border-red-500' : 'border-gray-300'
                          } ${isLoadingMasters ? 'bg-gray-50' : ''}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Sources...' : 'Select Source'}</option>
                        {masterSources.map((source) => (
                          <option key={source._id} value={source._id} className="capitalize">{source.name}</option>
                        ))}
                        {/* Show lead's existing source if it's inactive and not in the main list */}
                        {isEditMode && formData.source && !masterSources.some(s => s._id === mapLeadSourceToMasterId(formData.source, masterSources)) && (
                          <option value={leadSourceInfo?._id || formData.source}>
                            {leadSourceInfo?.name || formData.source} (Inactive)
                          </option>
                        )}

                      </select>
                      {!isLoadingMasters && masterSources.length === 0 && !formData.source && (
                        <p className="mt-1 text-xs text-amber-600">No active sources found in configuration.</p>
                      )}
                      {errors.source && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.source}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                        {editLeadId && originalStatus === 'closed_won' && (
                          <span className="ml-2 text-xs text-amber-600 font-normal">({closedWonStatusName} - status locked)</span>
                        )}
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value, 'select')}
                        disabled={editLeadId && originalStatus === 'closed_won'}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.status ? 'border-red-500' : (editLeadId && originalStatus === 'closed_won') ? 'border-gray-300 bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                          }`}
                      >
                        <option value="">Select Status</option>
                        {masterLeadStatuses.map((status) => (
                          <option key={status._id} value={status.value}>{status.name}</option>
                        ))}
                      </select>

                      {errors.status && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.status}
                        </p>
                      )}
                      {editLeadId && originalStatus === 'closed_won' && (
                        <p className="mt-1 text-xs text-amber-600">You can not change status of {closedWonStatusName} lead.</p>
                      )}

                    </div>

                    {isStatusChangedInEdit && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status Change Reason <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          rows={3}
                          value={formData.statusChangeReason}
                          onChange={(e) => handleInputChange('statusChangeReason', e.target.value, 'description', 1000)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.statusChangeReason ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder={`Why are you changing status from ${originalStatus} to ${formData.status}?`}
                          maxLength={1000}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.statusChangeReason && (
                            <p className="text-sm text-red-600 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {errors.statusChangeReason}
                            </p>
                          )}
                          <span className="text-xs text-gray-500 ml-auto">
                            {getCharacterCount(formData.statusChangeReason, 1000)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value, 'select')}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.category ? 'border-red-500' : 'border-gray-300'
                          }`}
                        disabled={isLoadingMasters}
                      >
                        <option value="">Select Category</option>
                        {masterLeadCategories.map((cat) => (
                          <option key={cat._id} value={cat._id} className='capitalize'>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                      {errors.category && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.category}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Source
                      </label>
                      <div className="flex items-center space-x-6 h-[42px]">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="companySource"
                            value="agrosaf"
                            checked={formData.companySource === 'agrosaf'}
                            onChange={(e) => handleInputChange('companySource', e.target.value)}
                            className="w-4 h-4 text-agrosaf-green focus:ring-agrosaf-green"
                          />
                          <span className="ml-2 text-sm text-gray-700 font-medium">Agrosaf</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="companySource"
                            value="alosafe"
                            checked={formData.companySource === 'alosafe'}
                            onChange={(e) => handleInputChange('companySource', e.target.value)}
                            className="w-4 h-4 text-agrosaf-green focus:ring-agrosaf-green"
                          />
                          <span className="ml-2 text-sm text-gray-700 font-medium">Alosafe</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Region
                      </label>
                      <select
                        value={formData.region}
                        onChange={(e) => handleInputChange('region', e.target.value, 'select')}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.region ? 'border-red-500' : 'border-gray-300'
                          }`}
                      >
                        <option value="">Select Region</option>
                        {regions.map((region) => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                      {errors.region && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.region}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Value
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(formData.leadValue)}
                        onChange={(e) => handleFormattedNumberChange('leadValue', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-all duration-200 ease-in-out ${errors.leadValue ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter lead value in ₹"
                        aria-invalid={!!errors.leadValue}
                        aria-describedby={errors.leadValue ? 'leadValue-error' : undefined}
                      />
                      {errors.leadValue && (
                        <p id="leadValue-error" className="mt-1 text-sm text-red-600 flex items-center transition-opacity duration-200">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.leadValue}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expected Order Value
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(formData.budget)}
                        onChange={(e) => handleFormattedNumberChange('budget', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-all duration-200 ease-in-out ${errors.budget ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter budget in ₹"
                        aria-invalid={!!errors.budget}
                        aria-describedby={errors.budget ? 'budget-error' : undefined}
                      />
                      {errors.budget && (
                        <p id="budget-error" className="mt-1 text-sm text-red-600 flex items-center transition-opacity duration-200">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {errors.budget}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timeline
                      </label>
                      <select
                        value={formData.timeline}
                        onChange={(e) => handleInputChange('timeline', e.target.value, 'select')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="">Select Timeline</option>
                        {timelines.map((timeline) => (
                          <option key={timeline} value={timeline}>{timeline}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Decision Maker
                      </label>
                      <select
                        value={formData.decisionMaker}
                        onChange={(e) => handleInputChange('decisionMaker', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${isLoadingMasters ? 'bg-gray-50' : 'border-gray-300'}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Decision Makers...' : 'Select Decision Maker'}</option>
                        {masterDecisionMakers.map((dm) => (
                          <option key={dm._id} value={dm._id}>{dm.name}</option>
                        ))}
                        {/* Show lead's existing decision maker if it's inactive and not in the main list */}
                        {isEditMode && formData.decisionMaker && leadDecisionMakerInfo && !masterDecisionMakers.find(d => d._id === formData.decisionMaker) && (
                          <option value={leadDecisionMakerInfo._id}>
                            {leadDecisionMakerInfo.name} (Inactive)
                          </option>
                        )}
                      </select>
                      {!isLoadingMasters && masterDecisionMakers.length === 0 && !formData.decisionMaker && (
                        <p className="mt-1 text-xs text-amber-600">No active decision makers found in configuration.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Temperature
                      </label>
                      <div className="flex items-center space-x-2 h-[42px]">
                        {[
                          { value: 'hot', label: 'Hot', icon: '🔥', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', accent: 'bg-red-600' },
                          { value: 'warm', label: 'Warm', icon: '☀️', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', accent: 'bg-orange-600' },
                          { value: 'cold', label: 'Cold', icon: '❄️', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', accent: 'bg-blue-600' }
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`
                              flex items-center px-3 py-1.5 rounded-lg border cursor-pointer transition-all duration-200
                              ${formData.scoreLabel === option.value
                                ? `${option.bg} ${option.border} shadow-sm border-2`
                                : 'bg-white border-gray-200 hover:border-gray-300'
                              }
                            `}
                          >
                            <input
                              type="radio"
                              name="scoreLabel"
                              value={option.value}
                              checked={formData.scoreLabel === option.value}
                              onChange={(e) => handleInputChange('scoreLabel', e.target.value)}
                              className="sr-only"
                            />
                            <span className="mr-1.5 text-sm">{option.icon}</span>
                            <span className={`text-xs font-bold capitalize ${formData.scoreLabel === option.value ? option.color : 'text-gray-500'}`}>
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Video Status
                      </label>
                      <select
                        value={formData.leadVideoStatus}
                        onChange={(e) => handleInputChange('leadVideoStatus', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${isLoadingMasters ? 'bg-gray-50' : 'border-gray-300'}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Video Statuses...' : 'Select Video Status'}</option>
                        {masterLeadVideoStatuses.map((status) => (
                          <option key={status._id} value={status._id} className='capitalize'>{status.name}</option>
                        ))}
                        {isEditMode && formData.leadVideoStatus && leadVideoStatusInfo && !masterLeadVideoStatuses.find(d => d._id === formData.leadVideoStatus) && (
                          <option value={leadVideoStatusInfo._id} className='capitalize'>
                            {leadVideoStatusInfo.name} (Inactive)
                          </option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Order Status
                      </label>
                      <select
                        value={formData.leadOrderStatus}
                        onChange={(e) => handleInputChange('leadOrderStatus', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${isLoadingMasters ? 'bg-gray-50' : 'border-gray-300'}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Order Statuses...' : 'Select Order Status'}</option>
                        {masterLeadOrderStatuses.map((status) => (
                          <option key={status._id} value={status._id} className='capitalize'>{status.name}</option>
                        ))}
                        {isEditMode && formData.leadOrderStatus && leadOrderStatusInfo && !masterLeadOrderStatuses.find(d => d._id === formData.leadOrderStatus) && (
                          <option value={leadOrderStatusInfo._id} className='capitalize'>
                            {leadOrderStatusInfo.name} (Inactive)
                          </option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Call Status
                      </label>
                      <select
                        value={formData.leadCallStatus}
                        onChange={(e) => handleInputChange('leadCallStatus', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${isLoadingMasters ? 'bg-gray-50' : 'border-gray-300'}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Call Statuses...' : 'Select Call Status'}</option>
                        {masterLeadCallStatuses.map((status) => (
                          <option key={status._id} value={status._id} className='capitalize'>{status.name}</option>
                        ))}
                        {isEditMode && formData.leadCallStatus && leadCallStatusInfo && !masterLeadCallStatuses.find(d => d._id === formData.leadCallStatus) && (
                          <option value={leadCallStatusInfo._id} className='capitalize'>
                            {leadCallStatusInfo.name} (Inactive)
                          </option>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lead Interest
                      </label>
                      <select
                        value={formData.leadInterest}
                        onChange={(e) => handleInputChange('leadInterest', e.target.value, 'select')}
                        disabled={isLoadingMasters}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${isLoadingMasters ? 'bg-gray-50' : 'border-gray-300'}`}
                      >
                        <option value="">{isLoadingMasters ? 'Loading Interests...' : 'Select Interest'}</option>
                        {masterLeadInterests.map((interest) => (
                          <option key={interest._id} value={interest._id} className='capitalize'>{interest.name}</option>
                        ))}
                        {isEditMode && formData.leadInterest && leadInterestInfo && !masterLeadInterests.find(d => d._id === formData.leadInterest) && (
                          <option value={leadInterestInfo._id} className='capitalize'>
                            {leadInterestInfo.name} (Inactive)
                          </option>
                        )}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product/Requirement
                      </label>
                      <textarea
                        value={formData.productRequirement}
                        onChange={(e) => handleInputChange('productRequirement', e.target.value, 'textarea', 500)}
                        rows={3}
                        placeholder="Specify product interest or requirement..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.productRequirement, 500)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'additional' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value, 'description', 1000)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        placeholder="Describe the lead's requirements and interests"
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.description, 1000)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remarks
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value, 'description', FIELD_LIMITS.notes)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        placeholder="Add any additional notes about this lead"
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(formData.notes, FIELD_LIMITS.notes)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={leadMutation.isPending}
            >
              {isEditMode ? 'Back' : 'Cancel'}
            </Button>
            <PermissionGate permission="lead_management" action={isEditMode ? "update" : "create"}>
              <Button
                type="submit"
                variant="primary"
                icon={<Save className="h-4 w-4" />}
                disabled={leadMutation.isPending || loading || isLoadingLead}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                {(leadMutation.isPending || loading)
                  ? (isEditMode ? 'Updating Lead...' : 'Creating Lead...')
                  : (isEditMode ? 'Update Lead' : 'Create Lead')}
              </Button>
            </PermissionGate>
          </div>
        </form>
      )
      }

    </div>
  );
};

export default ManualLeadEntry;
